import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Box, Typography, Alert, LinearProgress } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import '../styles/AgenticObservability.css';
import logger from '../utils/logger';

export default function AgenticObservability() {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [eventSource, setEventSource] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [status, setStatus] = useState({ status: '', message: '' });
  const [isConnected, setIsConnected] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const messagesEndRef = useRef(null);
  const statusTimestampRef = useRef(null); // Track when status was last set
  const statusQueueRef = useRef([]); // Queue for pending status updates
  const statusTimeoutRef = useRef(null); // Track pending status timeout
  const abortControllerRef = useRef(null); // Track abort controller for cancellation
  const readerRef = useRef(null); // Track reader for stream cancellation
  const MIN_STATUS_DISPLAY_DURATION = 1500; // Minimum milliseconds to display each status (1.5 seconds)

  // Global error handler for unhandled errors
  useEffect(() => {
    const handleError = (event) => {
      if (event.error?.name === 'AbortError') {
        event.preventDefault();
        logger.info('Caught global AbortError, preventing error boundary');
        setIsGenerating(false);
      }
    };
    
    const handleUnhandledRejection = (event) => {
      if (event.reason?.name === 'AbortError') {
        event.preventDefault();
        logger.info('Caught unhandled AbortError rejection, preventing error boundary');
        setIsGenerating(false);
      }
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Set current user in logger
  useEffect(() => {
    if (currentUser) {
      logger.setUser(currentUser);
    }
  }, [currentUser]);

  useEffect(() => {
    // Log status changes
    if (status.status) {
      logger.debug('Status state changed', { 
        statusType: status.status,
        statusMessage: status.message
      });
    }
  }, [status]);

  useEffect(() => {
    // Generate a unique session ID when the component first loads
    setSessionId(`agentic_session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);
    
    // Set connected status
    setIsConnected(true);
    
    return () => {
      cleanupEventSource();
      // Clear any pending status timeouts on unmount
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Only scroll to bottom when there are messages, not on initial load
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, show: true, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  const updateStatusWithMinimumDuration = (newStatus) => {
    const now = Date.now();
    const lastStatusTime = statusTimestampRef.current;
    
    // If no previous status or enough time has passed, show immediately
    if (!lastStatusTime || (now - lastStatusTime) >= MIN_STATUS_DISPLAY_DURATION) {
      setStatus(newStatus);
      statusTimestampRef.current = now;
      
      // Schedule processing of next queued status after minimum duration
      if (statusQueueRef.current.length > 0) {
        // Clear any existing timeout first
        if (statusTimeoutRef.current) {
          clearTimeout(statusTimeoutRef.current);
        }
        
        statusTimeoutRef.current = setTimeout(() => {
          const nextStatus = statusQueueRef.current.shift();
          if (nextStatus) {
            updateStatusWithMinimumDuration(nextStatus);
          }
        }, MIN_STATUS_DISPLAY_DURATION);
      } else if (newStatus.status === 'complete' || newStatus.status === '') {
        // If this is a final status and queue is empty, clear it after minimum duration
        if (statusTimeoutRef.current) {
          clearTimeout(statusTimeoutRef.current);
        }
        statusTimeoutRef.current = setTimeout(() => {
          setStatus({ status: '', message: '' });
          statusTimestampRef.current = null;
        }, MIN_STATUS_DISPLAY_DURATION);
      }
    } else {
      // Add this status to the queue (don't replace, append)
      statusQueueRef.current.push(newStatus);
      
      // If no timeout is pending, schedule one
      if (!statusTimeoutRef.current) {
        const timeToWait = MIN_STATUS_DISPLAY_DURATION - (now - lastStatusTime);
        statusTimeoutRef.current = setTimeout(() => {
          const queuedStatus = statusQueueRef.current.shift();
          if (queuedStatus) {
            updateStatusWithMinimumDuration(queuedStatus);
          }
        }, timeToWait);
      }
    }
  };

  const cleanupEventSource = () => {
    if (eventSource) {
      setEventSource(null);
    }
    // Cancel any ongoing fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Cancel any active reader
    if (readerRef.current) {
      readerRef.current.cancel();
      readerRef.current = null;
    }
  };

  const handleStopGeneration = () => {
    logger.info('User stopped generation', { sessionId });
    cleanupEventSource();
    setIsGenerating(false);
    updateStatusWithMinimumDuration({ status: '', message: '' });
    showToast('Generation stopped', 'info');
  };

  const setupEventSource = (url, token) => {
    try {
      cleanupEventSource();
      
      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();
      
      logger.debug('Initiating agentic observability stream request', { url, sessionId });
      
      const fetchSSE = async () => {
        try {
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'text/event-stream',
            },
            signal: abortControllerRef.current.signal
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body.getReader();
          readerRef.current = reader; // Store reader for cancellation
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            let readResult;
            try {
              readResult = await reader.read();
            } catch (readError) {
              // Handle abort during read
              if (readError.name === 'AbortError') {
                logger.info('Reader aborted during read', { sessionId });
                setIsGenerating(false);
                return;
              }
              throw readError;
            }
            
            const { done, value } = readResult;
            
            if (done) {
              logger.debug('Stream ended', { sessionId });
              setIsGenerating(false);
              setStatus({ status: '', message: '' });
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim() === '') continue;

              const eventMatch = line.match(/^event: (.+)\ndata: (.+)$/s);
              if (eventMatch) {
                const eventType = eventMatch[1];
                const eventData = eventMatch[2];

                try {
                  const data = JSON.parse(eventData);
                  handleSSEEvent(eventType, data);
                } catch (e) {
                  if (e.name === 'AbortError') {
                    logger.info('Event handling aborted', { sessionId });
                    return;
                  }
                  logger.error('Failed to parse SSE data', { error: e.message, eventData });
                }
              }
            }
          }
        } catch (error) {
          // Don't handle error if it's an abort (user cancelled)
          if (error.name === 'AbortError') {
            logger.info('Fetch aborted by user', { sessionId });
            setIsGenerating(false);
            return; // Don't retry on manual abort
          }
          logger.error('Error in SSE fetch', { error: error.message, stack: error.stack });
          
          // Handle stream errors gracefully without retrying
          setError('Connection interrupted. Please try sending your message again.');
          setIsGenerating(false);
          updateStatusWithMinimumDuration({ status: '', message: '' });
        }
      };

      fetchSSE().catch(error => {
        // Catch any unhandled promise rejections
        if (error.name !== 'AbortError') {
          logger.error('Unhandled error in fetchSSE', { error: error.message });
          setError('Connection error. Please try again.');
          setIsGenerating(false);
          updateStatusWithMinimumDuration({ status: '', message: '' });
        }
      });
    } catch (error) {
      logger.error('Error setting up EventSource', { error: error.message, stack: error.stack });
      setError('Failed to connect to the agent. Please try again.');
      setIsGenerating(false);
      updateStatusWithMinimumDuration({ status: '', message: '' });
    }
  };

  const handleSSEEvent = (eventType, data) => {
    logger.debug('SSE event received', { eventType, data });

    switch (eventType) {
      case 'connect':
        logger.info('Connected to SSE stream');
        updateStatusWithMinimumDuration({ status: 'connected', message: 'Connected to agent' });
        break;

      case 'sessionId':
        if (data.sessionId) {
          setSessionId(data.sessionId);
          logger.info('Session ID received from server', { sessionId: data.sessionId });
        }
        break;

      case 'chunk':
        // Append chunk to the last assistant message
        logger.debug('[AGENT RESPONSE CHUNK] Received chunk from agent', {
          sessionId,
          chunkLength: data.content?.length || 0,
          chunk: data.content
        });
        setMessages(prevMessages => {
          const newMessages = [...prevMessages];
          const lastMessage = newMessages[newMessages.length - 1];
          
          if (lastMessage && lastMessage.role === 'assistant') {
            lastMessage.content += data.content;
          } else {
            newMessages.push({
              role: 'assistant',
              content: data.content
            });
          }
          
          return newMessages;
        });
        break;

      case 'response':
        // Final response received
        logger.info('[AGENT RESPONSE] Final response received from agent', {
          sessionId,
          responseLength: data.content?.length || 0,
          agentResponse: data.content
        });
        setMessages(prevMessages => {
          const newMessages = [...prevMessages];
          const lastMessage = newMessages[newMessages.length - 1];
          
          if (lastMessage && lastMessage.role === 'assistant' && !lastMessage.content) {
            lastMessage.content = data.content;
          } else if (!lastMessage || lastMessage.role !== 'assistant') {
            newMessages.push({
              role: 'assistant',
              content: data.content
            });
          }
          
          return newMessages;
        });
        setIsGenerating(false);
        // Don't clear status immediately - let the queue system handle it
        // This ensures "Stored in Memory" and other final statuses show for full duration
        break;

      case 'status':
        updateStatusWithMinimumDuration({ status: data.status, message: data.message });
        break;

      case 'trace':
        // Handle trace events if needed for debugging
        logger.debug('Trace event', { trace: data });
        break;

      case 'error':
        logger.error('Error event from server', { error: data.error });
        setError(data.error);
        setIsGenerating(false);
        updateStatusWithMinimumDuration({ status: 'error', message: data.error });
        // Don't show toast for errors - the alert is enough
        break;

      default:
        logger.debug('Unknown event type', { eventType, data });
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputMessage
    };

    logger.info('[USER MESSAGE] User sent message', {
      sessionId,
      messageLength: inputMessage.length,
      userMessage: inputMessage,
      userEmail: currentUser?.email || 'anonymous'
    });

    setIsGenerating(true);
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputMessage('');
    setError(null);
    updateStatusWithMinimumDuration({ status: 'sending', message: 'Sending message...' });

    try {
      const token = localStorage.getItem('idToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Use URLSearchParams for proper query string encoding
      const queryParams = new URLSearchParams();
      queryParams.append('message', inputMessage);
      queryParams.append('sessionId', sessionId);

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const url = `${apiUrl}/agenticobs/stream?${queryParams.toString()}`;

      logger.debug('[UI → SERVER] Sending request with sessionId', {
        sessionId,
        messageLength: inputMessage.length,
        url: url.split('?')[0],
        userEmail: currentUser?.email || 'anonymous'
      });

      setupEventSource(url, token);
    } catch (error) {
      logger.error('Error sending message', { error: error.message, stack: error.stack });
      setError('Failed to send message. Please try again.');
      setIsGenerating(false);
      setStatus({ status: '', message: '' });
      showToast('Failed to send message', 'error');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'ready': '#4caf50',
      'connecting': '#2196f3',
      'processing': '#ff9800',
      'thinking': '#9c27b0',
      'observing': '#00bcd4',
      'complete': '#4caf50',
      'error': '#ff9800', // Use orange instead of red to reduce visual shock
      'retrying': '#ffc107',
      'sending': '#2196f3'
    };
    return colors[status] || '#757575';
  };

  return (
    <div className="chat-container agentic-observability-container">
      <div className="chat-header">
        <Typography variant="h5" component="h1">
          Agentic Observability
        </Typography>
        <div className="header-controls">
          <div className={`connection-indicator ${isConnected ? 'connected' : 'disconnected'}`} 
               title={isConnected ? 'Connected' : 'Disconnected'}>
          </div>
        </div>
      </div>

      <div className="chat-content-wrapper">
        {error && (
          <Alert 
            severity="warning" 
            onClose={() => setError(null)} 
            sx={{ 
              mb: 2, 
              mx: 2, 
              mt: 2,
              backgroundColor: '#fff3e0',
              color: '#e65100',
              '& .MuiAlert-icon': {
                color: '#f57c00'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {messages.length === 0 && (
          <div className="chat-welcome">
            <Typography variant="h5" className="welcome-title">
              AI-Powered Observability Assistant
            </Typography>
            <Typography variant="body1" className="welcome-subtitle">
              Ask questions about your system observability and get intelligent insights:
            </Typography>
            <ul className="welcome-examples">
              <li>"What are the most critical alerts in the last hour?"</li>
              <li>"Show me performance bottlenecks in the application"</li>
              <li>"Analyze error patterns across services"</li>
              <li>"What's causing the spike in response times?"</li>
            </ul>
          </div>
        )}

        <div className="messages-container">
          {messages.map((msg, idx) => (
            <div key={idx} className={`message ${msg.role}`}>
              <div className="message-bubble">
                {msg.role === 'assistant' ? (
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                ) : (
                  <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                    {msg.content}
                  </Typography>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Status indicator - fixed above input - always visible */}
      <div className="status-indicator">
        <div className="status-content">
          <div 
            className="status-badge" 
            style={{ backgroundColor: getStatusColor(status.status || 'ready') }}
          ></div>
          <span className="status-message">
            {status.status ? (
              <>{status.message}</>
            ) : (
              <><strong>Ready:</strong> Waiting for your question...</>
            )}
          </span>
        </div>
        {status.status && ['connecting', 'processing', 'thinking', 'observing', 'retrying'].includes(status.status) && (
          <LinearProgress 
            sx={{ 
              width: '100%', 
              height: 4, 
              marginTop: '8px',
              borderRadius: '2px',
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getStatusColor(status.status)
              }
            }} 
          />
        )}
      </div>

      <form onSubmit={handleSubmit} className="chat-input-form">
        <div className="input-container">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your observability data..."
            className="chat-input"
            disabled={isGenerating}
            rows={1}
          />
          <button
            type="submit"
            className="send-button"
            disabled={isGenerating || !inputMessage.trim()}
            title="Send message"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
          <button
            type="button"
            className="stop-button"
            disabled={!isGenerating}
            onClick={handleStopGeneration}
            title="Stop generation"
            style={{
              backgroundColor: '#f44336',
              opacity: isGenerating ? 1 : 0.4,
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isGenerating ? 'pointer' : 'not-allowed'
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <rect x="8" y="8" width="8" height="8" rx="1" fill="white" stroke="none" />
            </svg>
          </button>
        </div>
      </form>

      {toast.show && (
        <div className={`toast ${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
